/**
 * SidebarSubsection — level-2 and level-3 collapsible groups.
 * Recursive: if children present, renders nested <SidebarSubsection>; if leaves,
 * renders <SidebarItem>s. Chevron rotates 90° when open.
 */
import { Chevron } from "./icons";
import { SidebarItem } from "./SidebarItem";
import type { SidebarLeaf, SidebarNode } from "../../lib/sidebar-tree";

export interface SidebarSubsectionProps {
	node: SidebarNode;
	depth: number;
	open: boolean;
	onToggle: () => void;
	activeHref: string;
	// Flat render of all descendant leaves when provided (for search mode).
	renderAsFlat?: SidebarLeaf[];
	// Path from section down to this node (for stable keys).
	path: string;
	// Map of all open paths (to support nested toggles).
	openPaths: Set<string>;
	toggleOpen: (path: string) => void;
}

export function SidebarSubsection({
	node,
	depth,
	open,
	onToggle,
	activeHref,
	renderAsFlat,
	path,
	openPaths,
	toggleOpen,
}: SidebarSubsectionProps) {
	const pl = 12 + depth * 16;
	const hasChildren = Array.isArray(node.children) && node.children.length > 0;
	const leaves = node.leaves ?? [];

	return (
		<li role="none">
			<button
				type="button"
				role="treeitem"
				aria-expanded={open}
				aria-level={depth + 2}
				onClick={onToggle}
				className="w-full flex items-center gap-1.5 py-1.5 pr-3 text-[13px] text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 text-left"
				style={{ paddingLeft: pl }}
			>
				<Chevron
					className={
						open
							? "w-3 h-3 text-zinc-500 transition-transform duration-150 rotate-90"
							: "w-3 h-3 text-zinc-400 transition-transform duration-150"
					}
				/>
				<span className="truncate flex-1">{node.label}</span>
				<span className="text-[10px] text-zinc-400 tabular-nums">
					{renderAsFlat
						? renderAsFlat.length
						: hasChildren
							? leaves.length +
								(node.children?.reduce(
									(n, c) =>
										n +
										(c.leaves?.length ?? 0) +
										(c.children?.reduce(
											(n2, cc) => n2 + (cc.leaves?.length ?? 0),
											0,
										) ?? 0),
									0,
								) ?? 0)
							: leaves.length}
				</span>
			</button>

			{open ? (
				<ul role="group" className="flex flex-col">
					{/* Nested subsections */}
					{hasChildren
						? node.children?.map((child) => {
								const childPath = `${path}/${child.label}`;
								return (
									<SidebarSubsection
										key={childPath}
										node={child}
										depth={depth + 1}
										open={openPaths.has(childPath)}
										onToggle={() => toggleOpen(childPath)}
										activeHref={activeHref}
										path={childPath}
										openPaths={openPaths}
										toggleOpen={toggleOpen}
									/>
								);
							})
						: null}
					{/* Leaf children — render even when node also has children (mixed mode) */}
					{leaves.length > 0
						? leaves.map((leaf, i) => (
								<SidebarItem
									key={`${leaf.slug}|${leaf.href}|${i}`}
									leaf={leaf}
									depth={depth + 1}
									active={leaf.href === activeHref}
								/>
							))
						: null}
				</ul>
			) : null}
		</li>
	);
}
